/**
 * Asistente de campaña del WhatsApp oficial, en 4 pasos (`campanas/nueva` y `campanas/:id/editar`): «A quién», «Qué les
 * decís» (la plantilla y sus variables), «Cuándo» (y cómo se dan de baja) y «Revisar y mandar» (la prueba y el confirmar).
 * Antes eran seis (R2 del rediseño, 29/10/2026); los nombres de antes siguen llegando en links viejos y llevan al paso
 * nuevo (`ALIAS_PASOS`).
 *
 * - El borrador vive EN EL SERVIDOR (campaña en estado `borrador`): nace en «A quién» con la audiencia y cada paso guarda
 *   al avanzar; un F5 retoma desde la base. Nada de localStorage para el borrador (a diferencia del wizard de Activa,
 *   que guarda CSV con datos personales en el navegador). La prueba ya mandada también sale de la base: de la auditoría
 *   de la campaña (`pruebaDeLaAuditoria`), así un F5 en «Revisar y mandar» no pide otra.
 * - Los pasos van POR NOMBRE en `?paso=` (misma lección que `CampanaNuevaRealPage.tsx`: con índices, agregar un paso
 *   corría todos y un `current === 4` mostraba otro paso). El paso de la URL se respeta sólo si es alcanzable con lo que
 *   la campaña ya tiene guardado.
 * - La previa, el chequeo y el costo los calcula el backend con la MISMA función que el envío:
 *   el front nunca arma el texto que va a salir (previa ≡ envío).
 * - Un 200 de la prueba no es un éxito por sí solo: Twilio rechaza con 200 y `estado: "fallido"`.
 *   El título sale de `tituloPrueba` (logic/chequeo.ts) y nunca dice «Mandada» por un 200 a secas.
 * - Confirmar manda la huella del chequeo: si algo cambió (una baja, el saldo, el cupo), el backend
 *   responde 409 `CHEQUEO_DESACTUALIZADO` con el chequeo nuevo, que reemplaza al mostrado. No se
 *   reintenta solo: el operador tiene que ver los números nuevos antes de gastar.
 * - «Revisar y mandar» arranca con «Vas a mandar» (`components/VasAMandar.tsx`): la previa real, la plantilla, a quiénes
 *   en palabras, cuántos, cuándo y el costo máximo. Debajo, la prueba: se manda con una que llegó o con «Seguir sin
 *   probar». «Ahora» o «Programar» se elige a propósito en «Cuándo» (no hay uno por defecto) y un envío grande
 *   (`logic/vasAMandar.ts`) pide escribir la cantidad de personas antes de mandar.
 * - Plantilla en el momento (SPEC-BD C1-C2): desde «Qué les decís» se crea una plantilla, se parte de un
 *   modelo o se duplica una, en una PÁGINA aparte (`campanas/:id/plantilla?editor=`, ver
 *   `logic/editorCampana.ts`), nunca en una ventana encima del asistente: se trabaja más cómodo y el
 *   «atrás» del navegador vuelve al editor. La campaña ya está guardada, así la plantilla queda asociada apenas se crea;
 *   al volver, la página deja su aviso en `location.state`. Ir allá desmonta el asistente: lo elegido sin guardar (la
 *   plantilla, la prueba) viaja en sessionStorage el tiempo del viaje (`FotoAsistente`). `?publico=` va y vuelve por la
 *   URL. Con la plantilla en revisión se completan las variables y «Cuándo»; «Revisar y mandar» espera la aprobación
 *   (`maxPasoAlcanzable`). Nada se manda solo cuando Meta la aprueba.
 * - A quién (SPEC-BD D8): todos los clientes (el Público de Activa), una lista guardada (un público de la base), un archivo
 *   o los grupos de una segmentación de Activa. Con un público de la base las variables pueden usar los campos
 *   personalizados de la marca. En ninguno hay grupo de control: WhatsApp oficial le escribe a todos los incluidos
 *   (decisión de Alan, 23/09).
 * - «Borrar borrador» (27/10/2026), arriba a la derecha con la campaña ya guardada: confirmación en la misma pantalla
 *   (`AccionesCampana`) y vuelta a la lista con el aviso. Un borrador duplicado llega acá sin `?paso=` y se retoma como
 *   cualquiera: el paso más lejano que permite lo guardado, mirando todo de nuevo. Con el horario y la baja copiados de
 *   la original (29/10/2026) llega a «Revisar y mandar»: «Ahora» o «Programar» se elige ahí mismo (nunca se copia) y la
 *   prueba empieza de cero, porque es otra campaña.
 * - «Por compras» y «Excluir» (28/10/2026), en todos los clientes y la lista guardada: dos bloques plegados debajo de lo
 *   que se eligió. Las opciones cuentan con los dos (el mismo cálculo del chequeo) y se guardan con la audiencia sólo si
 *   dicen algo. Con una lista de exclusión que se borró, «Siguiente» no avanza hasta sacarla.
 * - Un seguimiento (28/10/2026) llega con todo lo de la original: en «A quién» no se elige nada (la fija la original,
 *   `AudienciaSeguimiento` dice el criterio y cuántos quedan hoy) y «Siguiente» sigue sin guardar.
 * - La página sólo arma el asistente (R1 del rediseño, 29/10/2026): el estado y lo que guarda cada paso viven en
 *   `hooks/asistente/` (`useBorradorCampana`, `usePasoEnUrl`, `usePruebaCampana`, `useVariablesCampana`,
 *   `useConfirmarCampana` y los de la plantilla, la audiencia y el horario) y lo que se ve, en `components/asistente/`.
 *   Cada paso arma su botón (`cta`: qué hace «Siguiente» y, si todavía no se puede seguir, por qué).
 * - «Tu campaña» (29/10/2026), en todos los pasos: a la derecha en la computadora y en la barra de abajo en el celular.
 *   El nombre se cambia ahí. Muestra lo que ya calcularon los pasos (`useTuCampana`): no pide ningún chequeo. En «A quién»,
 *   lo elegido que todavía no se guardó, con la cuenta de la pantalla.
 * - «Armar en el oficial» (`?analisis=`, 29/10/2026), con borradores de esa segmentación, los muestra antes de «A quién»
 *   para seguir uno o armar otra (`useBorradorDelAnalisis`, `ElegirDelAnalisis`): se arma una campaña por grupo. Sin
 *   borradores, o con «Armar otra», el borrador nace acá como siempre y, si ya salieron otras con esa audiencia, se avisa
 *   cuáles y con qué grupos. Los que dejó «Preparar el envío» (una campaña por plantilla, R3) aparecen ahí también; uno
 *   que ya trae la plantilla y el mapeo abre en el primer paso que le falta (`maxPasoAlcanzable`).
 * - Una repetición que quedó en borrador (29/10/2026) se reconoce: arriba y en el panel, «Repetición N de …» con el link a
 *   la original; en «Cuándo», «Programar» viene con la fecha que le toca (sin marcarlo); y al lado de «Seguir sin probar»
 *   dice si la serie ya se probó con esta plantilla. La prueba sigue siendo de ésta: la de otra nunca se toma sola.
 * - Lo que se ve en «Revisar y mandar» es lo que sale (29/10/2026): la plantilla, el horario y la baja GUARDADOS en la
 *   campaña («Cuándo» también usa la plantilla guardada). Saltar de paso (el stepper, «Tu campaña», «Atrás») con algo sin
 *   guardar pregunta antes en la misma pantalla (`PreguntaSinGuardar`): guardar y seguir, descartar o quedarse.
 * - Si otra pestaña cambió la campaña, «Revisar y mandar» lo dice (29/10/2026): al entrar y ante un 409 la vuelve a traer
 *   y, si cambió algo que no son cifras, avisa cómo quedó y la prueba empieza de cero si cambió la plantilla.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, Zap } from "lucide-react";
import { useCuentaOficial } from "../contexto";
import type { EstadoPlantilla, PlantillaDetalle, PlantillaResumen } from "../types";
import type { PasoAsistente } from "../logic/chequeo";
import { ESTADOS_PLANTILLA_PARA_SEGUIR, PASOS } from "../logic/chequeo";
import { usd } from "../logic/formato";
import {
  avisoDelAnalisis, idx, leerVuelta, origenDeLaCopia, textoCambioEnOtraPestana, textoPruebaDeLaCopia, type Aviso, type CambiosDelPaso, type CtaPaso,
} from "../logic/asistente";
import { NOMBRE_DEL_PASO } from "../logic/tuCampana";
import { textoPruebaDeLaSerie } from "../logic/repeticion";
import { textoDelBorradorDeDisparador } from "../logic/disparadores";
import { EstadoPlantillaEnVivo } from "../components/EstadoPlantillaEnVivo";
import { BorrarBorrador } from "../components/AccionesCampana";
import { RenglonRepeticion } from "../components/RepeticionCampana";
import { AvisoRojo } from "../components/asistente/AvisoRojo";
import { PasoPlantilla, PreguntaCambioPlantilla } from "../components/asistente/PasoPlantilla";
import { ElegirAudiencia } from "../components/asistente/ElegirAudiencia";
import { ElegirDelAnalisis } from "../components/asistente/ElegirDelAnalisis";
import { PasoVariables } from "../components/asistente/PasoVariables";
import { PasoCuando } from "../components/asistente/PasoCuando";
import { PasoPrueba } from "../components/asistente/PasoPrueba";
import { ElegirCuando } from "../components/asistente/ElegirCuando";
import { PasoConfirmar } from "../components/asistente/PasoConfirmar";
import { BarraDelPaso } from "../components/asistente/BarraDelPaso";
import { PreguntaSinGuardar } from "../components/asistente/PreguntaSinGuardar";
import { ResumenEnLaBarra, TuCampana, TuCampanaEnElCelular } from "../components/asistente/TuCampana";
import { usePasoEnUrl } from "../hooks/asistente/usePasoEnUrl";
import { usePruebaCampana } from "../hooks/asistente/usePruebaCampana";
import { useBorradorCampana } from "../hooks/asistente/useBorradorCampana";
import { useVariablesCampana } from "../hooks/asistente/useVariablesCampana";
import { useConfirmarCampana } from "../hooks/asistente/useConfirmarCampana";
import { useAudienciaCampana } from "../hooks/asistente/useAudienciaCampana";
import { useHorarioCampana } from "../hooks/asistente/useHorarioCampana";
import { usePlantillaCampana } from "../hooks/asistente/usePlantillaCampana";
import { useTuCampana } from "../hooks/asistente/useTuCampana";
import { TEXTO_NO_SE_PUDO_BUSCAR, useBorradorDelAnalisis } from "../hooks/asistente/useBorradorDelAnalisis";
import WizardStepper from "../../campana-nueva/components/WizardStepper";
import type { Fase, Paso } from "../../campana-nueva/components/WizardStepper";

export { PREGUNTA_CAMBIO_PLANTILLA } from "../components/asistente/PasoPlantilla";

const B = "/activa/oficial";
const RUTA_CONFIG = `${B}/configuracion`;

const STEPS: (Paso & { key: PasoAsistente })[] = [
  { key: "audiencia", label: NOMBRE_DEL_PASO.audiencia, desc: "A quiénes les llega" },
  { key: "mensaje", label: NOMBRE_DEL_PASO.mensaje, desc: "La plantilla y el dato que va en cada variable" },
  { key: "cuando", label: NOMBRE_DEL_PASO.cuando, desc: "Cuándo sale y cómo se dan de baja" },
  { key: "revisar", label: NOMBRE_DEL_PASO.revisar, desc: "La prueba, a cuántos y cuánto cuesta" },
];
// Una fase por paso: con cuatro, cada uno es una etapa en sí (antes, seis pasos en cuatro fases).
const FASES: Fase[] = STEPS.map((s, i) => ({ key: s.key, label: s.label, pasos: [i] }));

export default function CampanaAsistentePage() {
  const { id: idRuta } = useParams();
  const [sp, setSp] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { cargando: cargandoCuenta, config, modo, error: errorCuenta, cuenta, recargar } = useCuentaOficial();
  // Se lee UNA vez al montar: el efecto de abajo lo saca del historial (mismo patrón que el detalle de la campaña).
  const [vuelta] = useState(() => leerVuelta(location.state));
  const [errorPaso, setErrorPaso] = useState<Aviso | null>(vuelta.error ? { texto: vuelta.error } : null);
  const [avisoOk, setAvisoOk] = useState(vuelta.aviso);
  const limpiarAvisos = useCallback(() => { setErrorPaso(null); setAvisoOk(""); }, []);
  // «Tu campaña» abierta encima, en el celular
  const [panelAbierto, setPanelAbierto] = useState(false);
  const cerrarPanel = useCallback(() => setPanelAbierto(false), []);
  /** El salto a otro paso que espera la respuesta de `PreguntaSinGuardar` (desde qué paso y adónde). */
  const [salto, setSalto] = useState<{ desde: PasoAsistente; a: PasoAsistente } | null>(null);
  // Público de la base: `?publico=` llega de «Usar en una campaña» y viaja en todo el recorrido (R-B12).
  const publicoUrl = sp.get("publico") || "";
  const analisisUrl = sp.get("analisis") || "";

  // El aviso del editor ya se leyó (`vuelta`): se saca del historial para que un F5 o un «atrás» no lo repitan. Va antes
  // que `usePasoEnUrl`: los efectos corren en orden, y la URL que deja este (la de al montar) la corrige después el del
  // paso si llegó con un nombre viejo (`?paso=plantilla` de la vuelta del editor).
  useEffect(() => {
    if (vuelta.aviso || vuelta.error) navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    // Sólo al montar, como en el detalle de la campaña.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── El paso, la prueba y el borrador ─────────────────────────────────────
  const { paso, maxAlcanzado, setMaxAlcanzado, irA } = usePasoEnUrl({ sp, setSp, alIr: limpiarAvisos });
  // «Armar en el oficial» (`?analisis=`): si esa segmentación ya tiene borradores, se elige uno o se arma otra
  const delAnalisis = useBorradorDelAnalisis({ idRuta, analisisUrl });
  const prueba = usePruebaCampana();
  const borrador = useBorradorCampana({ idRuta, sp, modo, publicoUrl, irA, setMaxAlcanzado, prueba });
  const { campana, detalle, plantillaId, estadoElegida, plantillaResumen } = borrador;
  // Las variables son de la plantilla que la campaña tiene guardada, si sirve: con otra elegida y sin guardar, o con una
  // rechazada (o de otra cuenta), todavía no. Cada cálculo anota en la auditoría.
  const conVariables = !!plantillaId && campana?.plantilla?.id === plantillaId && !borrador.elegidaInutilizable
    && !!estadoElegida && ESTADOS_PLANTILLA_PARA_SEGUIR.includes(estadoElegida) && !borrador.elegidaArchivada;

  // ── Cada paso ────────────────────────────────────────────────────────────
  // Los que guardan van después de los que dependen de lo guardado: al guardar, descartan lo calculado con lo de antes.
  const variables = useVariablesCampana({
    paso, campana, registrarGuardado: borrador.registrarGuardado, detalle: conVariables ? detalle : undefined, irA,
    camposListos: borrador.camposListos, camposFallaron: borrador.camposFallaron, camposValidos: borrador.camposValidos,
  });
  /**
   * La campaña vuelta a traer, por si otra pestaña la cambió (al entrar a «Revisar y mandar» y ante un 409): con otra
   * plantilla, la prueba y las variables eran de la anterior; con otras variables u otra audiencia, lo calculado con ellas.
   */
  const releerCampana = async () => {
    const r = await borrador.releer();
    if (!r) return null;
    if (r.confirmada || !r.cambios.length) return { texto: null, confirmada: r.confirmada };
    if (r.cambios.includes("plantilla")) { prueba.reiniciar(); variables.reiniciar(true); }
    else if (r.cambios.includes("mapeo") || r.cambios.includes("audiencia")) variables.reiniciar();
    return { texto: textoCambioEnOtraPestana(r.cambios, r.campana, borrador.audiencias), confirmada: false };
  };
  // «Revisar y mandar» repasa y manda lo guardado: el horario y la baja de la campaña, no lo editado en «Cuándo».
  const confirmacion = useConfirmarCampana({
    paso, idCampana: borrador.idCampana, cargando: borrador.cargandoCampana, horarioEd: borrador.horarioEd,
    guardado: { horario: borrador.horarioGuardado, horarioElegido: borrador.horarioElegido, baja: borrador.bajaGuardada },
    cuenta, pruebaOk: prueba.ok, sinPrueba: prueba.sinPrueba, previa: variables.previa, setPrevia: variables.setPrevia, releer: releerCampana,
    fechaDeLaRepeticion: borrador.campana?.repeticionDe?.programadaPara ?? null,
  });
  const audiencia = useAudienciaCampana({
    paso, borrador, modo, publicoUrl, analisisUrl, setErrorPaso, irA,
    alGuardar: () => { variables.reiniciar(); confirmacion.descartarChequeo(); },
  });
  // «Cuándo» es de la plantilla guardada: la baja se elige con lo que trae la que va a salir.
  const horarioYBaja = useHorarioCampana({
    campana, eleccionBaja: borrador.eleccionBaja, horarioEd: borrador.horarioEd, guardado: { horario: borrador.horarioGuardado, baja: borrador.bajaGuardada },
    detalle: borrador.detalleGuardada, plantillaAprobada: borrador.estadoGuardada === "approved", config, modo, recargar,
    cuando: confirmacion.cuando, horario: confirmacion.horario.paraEnviar, horarioIncompleto: confirmacion.horario.incompleto, guardando: borrador.guardando,
    registrarGuardado: borrador.registrarGuardado, descartar: borrador.descartarHorario, setGuardando: borrador.setGuardando, setErrorPaso, setAvisoOk, irA,
    alGuardar: confirmacion.descartarChequeo,
  });
  const plantilla = usePlantillaCampana({
    borrador, config, modo, idRuta, sp, setSp, publicoUrl, prueba, setMaxAlcanzado, irA, setErrorPaso, setAvisoOk,
    antesDeCambiarPlantilla: variables.detener,
    antesDeIrAlEditor: variables.vaciar,
    alCambiarPlantilla: () => { variables.reiniciar(true); prueba.reiniciar(); confirmacion.descartarChequeo(); },
    descartarChequeo: confirmacion.descartarChequeo,
  });
  const tuCampana = useTuCampana({
    paso, borrador, audiencia: { enPantalla: audiencia.enPantalla, motivo: audiencia.cta.motivo },
    chequeo: confirmacion.chequeo, chequeoVars: variables.chequeoVars, previa: variables.previa, cuando: confirmacion.cuando,
    cuenta, config, modo,
  });

  // ── Render ───────────────────────────────────────────────────────────────
  if (cargandoCuenta || borrador.cargandoCampana || delAnalisis.buscando) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="h-24 rounded-2xl bg-slate-100 animate-pulse" />
        <div className="h-64 rounded-2xl bg-slate-100 animate-pulse" />
      </div>
    );
  }
  if (!config) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-sm text-red-700" role="alert">
        <p className="font-semibold">No pudimos traer la configuración de WhatsApp oficial.</p>
        {errorCuenta && <p className="mt-1">{errorCuenta}</p>}
      </div>
    );
  }
  if (borrador.noEncontrada) {
    return (
      <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center">
        <p className="text-sm text-slate-600">No encontramos esta campaña.</p>
        <Link to={`${B}/campanas`} className="mt-3 inline-block text-sm font-semibold text-violet-600 hover:underline">Volver a las campañas</Link>
      </div>
    );
  }
  if (borrador.errorCarga) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-sm text-red-700" role="alert">
        <p className="font-semibold">No pudimos traer la campaña.</p>
        <p className="mt-1">{borrador.errorCarga}</p>
      </div>
    );
  }
  // con borradores de esa segmentación, antes de «A quién»: seguir uno o armar otra (una campaña por grupo)
  if (delAnalisis.elegir.length) {
    return <ElegirDelAnalisis borradores={delAnalisis.elegir} audiencias={borrador.audiencias} onArmarOtra={delAnalisis.armarOtra} />;
  }

  const paso0 = STEPS[idx(paso)];
  // El botón de cada paso: qué hace «Siguiente» y, si todavía no se puede seguir, por qué. En «Qué les decís», el de la
  // plantilla hasta que está guardada; después, el de sus variables (lo que falte de la plantilla, primero).
  const ctas: Record<PasoAsistente, CtaPaso> = {
    // sin saber si la segmentación ya tiene un borrador, no se arma otro a ciegas
    audiencia: delAnalisis.error && !campana ? { ...audiencia.cta, motivo: "Falta revisar si ya hay un borrador: reintentá arriba" } : audiencia.cta,
    mensaje: plantilla.guardada ? { ...variables.cta, motivo: plantilla.cta.motivo ?? variables.cta.motivo } : plantilla.cta,
    cuando: horarioYBaja.cta,
    revisar: confirmacion.cta,
  };
  // Lo que cada paso tiene sin guardar: saltar a otro sin «Siguiente» pregunta antes. En «Qué les decís», la plantilla
  // elegida primero (otra plantilla borra las variables); si no, las variables.
  const cambios: Record<PasoAsistente, CambiosDelPaso | null> = {
    audiencia: audiencia.cambios,
    mensaje: plantilla.cambios.sinGuardar ? plantilla.cambios : variables.cambios,
    cuando: horarioYBaja.cambios,
    revisar: null,
  };
  /**
   * El stepper, «Tu campaña», «Atrás» y los links de cada paso: con algo sin guardar en éste, primero la pregunta. Desde
   * «Qué les decís», las variables que esperaban su debounce se guardan antes de irse (si no se pudo, también se pregunta).
   */
  const saltarA = async (p: PasoAsistente) => {
    if (p === paso) return;
    const vaciado = paso === "mensaje" ? await variables.vaciar() : true;
    if (!vaciado || cambios[paso]?.sinGuardar) { setSalto({ desde: paso, a: p }); return; }
    setSalto(null);
    irA(p);
  };
  // La pregunta vale en el paso donde se hizo y mientras quede algo sin guardar: con «Siguiente» o con lo cambiado
  // deshecho a mano, se va (y no vuelve sola con el próximo cambio).
  const cambiosDelSalto = salto && salto.desde === paso ? cambios[paso] : null;
  if (salto && !cambiosDelSalto?.sinGuardar) setSalto(null);
  const pregunta = salto && cambiosDelSalto?.sinGuardar ? { a: salto.a, cambios: cambiosDelSalto } : null;
  const atras = () => {
    const i = idx(paso);
    if (i === 0) navigate(`${B}/campanas`);
    else void saltarA(PASOS[i - 1]);
  };
  // El estado en vivo de una plantilla: la elegida debajo de su previa en «Qué les decís» y, mientras Meta no aprueba la
  // guardada, ésa arriba de «Cuándo» (avisa sola cuando la aprueban, y ahí se puede seguir).
  const estadoEnVivoDe = (id: string, estado: EstadoPlantilla | undefined, det: PlantillaDetalle | undefined, res: PlantillaResumen | undefined) => id && estado ? (
    <EstadoPlantillaEnVivo
      plantilla={{ id, estado, rejectionReason: det?.rejectionReason ?? res?.rejectionReason, nombreVisible: det?.nombreVisible || res?.nombreVisible || "" }}
      modo={modo ?? "twilio"}
      onCambio={plantilla.alCambiarEstado}
      onCorregir={() => void plantilla.duplicarYEditar(id)}
      onSeguirEditando={() => void plantilla.abrirEditor(`plantilla:${id}`)}
      corrigiendo={plantilla.duplicandoId === id}
    />
  ) : null;
  const estadoEnVivo = estadoEnVivoDe(plantillaId, estadoElegida, detalle, plantillaResumen);
  const guardadaId = campana?.plantilla?.id || "";
  const { chequeo, cuando } = confirmacion;
  const horario = confirmacion.horario.paraEnviar;
  const copiaDe = origenDeLaCopia(campana);
  const panel = (alIr?: () => void) => (
    <TuCampana
      datos={tuCampana.datos}
      onGuardarNombre={tuCampana.guardarNombre}
      paso={paso}
      alcanzable={(p) => !!campana && idx(p) <= maxAlcanzado}
      onIrA={(p) => { alIr?.(); void saltarA(p); }}
    />
  );
  // La franja con la que sale: la propia (como la dice el chequeo) o la de la marca.
  const franjas = (horario?.franjas && chequeo?.franjas) || config.franjasTexto || chequeo?.franjas || "";
  // «Ahora» o «Programar»: en «Cuándo» y, si se llegó sin elegir, en «Revisar y mandar»
  const elegirCuando = (
    <ElegirCuando modo={cuando.modo} onModo={cuando.setModo} local={cuando.local} onLocal={cuando.setLocal} limites={cuando.limites} zona={cuando.zona} franjas={franjas}
      sugerida={cuando.sugerida} fueraDeRango={cuando.fueraDeRango} />
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to={`${B}/campanas`} className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
            <ArrowLeft size={13} /> Campañas
          </Link>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight mt-1">{campana ? campana.nombre : "Nueva campaña"}</h2>
          {/* una repetición que quedó en borrador: de qué serie es, cuándo le toca y por qué no salió sola */}
          {campana?.repeticionDe && <RenglonRepeticion campana={campana} />}
          {/* un borrador de un disparador: de cuál es y con qué base de ventas se armó (su atraso), antes de confirmarlo */}
          {campana?.disparadorDe && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500" data-testid="borrador-de-disparador">
              <Zap size={12} className="flex-shrink-0 text-violet-500" aria-hidden="true" />
              <span>{textoDelBorradorDeDisparador(campana.disparadorDe)}</span>
            </p>
          )}
        </div>
        {campana && (
          <BorrarBorrador id={campana.id} nombre={campana.nombre}
            onBorrada={() => navigate(`${B}/campanas`, { state: { aviso: `Se borró el borrador «${campana.nombre}».` } })} />
        )}
      </div>

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,300px)] lg:items-start lg:gap-6">
        <div className="min-w-0 space-y-5">
          <WizardStepper
            steps={STEPS}
            fases={FASES}
            current={idx(paso)}
            maxReached={maxAlcanzado}
            onGo={(i) => { if (i <= maxAlcanzado && campana) void saltarA(PASOS[i]); }}
          />

          <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-5">
            {/* Sin «Paso N de M» acá: ya lo dice el WizardStepper de arriba (eran tres indicadores del mismo progreso). */}
            <header>
              <h3 className="text-lg font-semibold text-slate-900 tracking-tight">{paso0.label}</h3>
              <p className="text-sm text-slate-500">{paso0.desc}</p>
            </header>

            {pregunta && (
              <PreguntaSinGuardar
                paso={paso}
                noSeGuarda={pregunta.cambios.noSeGuarda}
                guardando={borrador.guardando}
                onGuardar={() => { setSalto(null); void pregunta.cambios.guardar(pregunta.a); }}
                onDescartar={() => { setSalto(null); pregunta.cambios.descartar(pregunta.a); }}
                onQuedarme={() => setSalto(null)}
              />
            )}

            {errorPaso && <AvisoRojo aviso={errorPaso} />}
            {confirmacion.avisoOtraPestana && (
              <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert" data-testid="aviso-otra-pestana">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" /> {confirmacion.avisoOtraPestana}
              </p>
            )}
            {avisoOk && (
              <p className="flex items-start gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
                <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" /> {avisoOk}
              </p>
            )}
            {plantilla.avisoRechazada && (
              <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                <span>{plantilla.avisoRechazada}</span>
                {plantillaId && (
                  <button type="button" onClick={() => void plantilla.duplicarYEditar(plantillaId)} disabled={!!plantilla.duplicandoId} className="font-semibold underline disabled:opacity-50">Duplicar y editar</button>
                )}
              </div>
            )}

            {paso === "audiencia" && !campana && delAnalisis.salieron.length > 0 && (
              <div className="flex items-start justify-between gap-3 flex-wrap rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status" data-testid="aviso-ya-salio">
                <span className="flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" /> {avisoDelAnalisis(delAnalisis.salieron, borrador.audiencias)}</span>
                <Link to={`${B}/campanas/${encodeURIComponent(delAnalisis.salieron[0].id)}`} className="font-semibold underline whitespace-nowrap">
                  {delAnalisis.salieron.length > 1 ? "Ver la última" : "Ver la campaña"}
                </Link>
              </div>
            )}
            {paso === "audiencia" && !campana && delAnalisis.error && (
              <div className="flex items-start justify-between gap-3 flex-wrap rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                <span>{TEXTO_NO_SE_PUDO_BUSCAR} {delAnalisis.error}</span>
                <button type="button" onClick={delAnalisis.reintentar} className="font-semibold underline">Reintentar</button>
              </div>
            )}
            {paso === "audiencia" && <ElegirAudiencia borrador={borrador} audiencia={audiencia} />}

            {paso === "mensaje" && (
              <>
                <PasoPlantilla
                  plantillas={borrador.plantillas}
                  error={borrador.errorPlantillas}
                  plantillaId={plantillaId}
                  onElegir={plantilla.elegirPlantilla}
                  detalle={detalle}
                  errorDetalle={borrador.errorDetalle}
                  modo={modo}
                  asociadaId={campana?.plantilla?.id || ""}
                  onCrear={() => void plantilla.abrirEditor("nueva")}
                  onModelos={() => void plantilla.abrirEditor("modelos")}
                  onDuplicar={(id) => void plantilla.duplicarYEditar(id)}
                  onSeguirEditando={(id) => void plantilla.abrirEditor(`plantilla:${id}`)}
                  duplicandoId={plantilla.duplicandoId}
                  estadoEnVivo={estadoEnVivo}
                />
                {plantilla.preguntarCambio && (
                  <PreguntaCambioPlantilla onSi={() => void plantilla.guardarPlantilla(plantillaId, true)} onNo={plantilla.noCambiar} guardando={borrador.guardando} />
                )}
                {conVariables && (
                  <div className="border-t border-slate-100 pt-5 space-y-3">
                    <h4 className="text-base font-semibold text-slate-900 tracking-tight">Lo que va en cada variable</h4>
                    <PasoVariables
                      detalle={detalle}
                      errorDetalle={borrador.errorDetalle}
                      variables={variables.variablesMapeables}
                      campos={borrador.campos}
                      mapeo={variables.mapeo}
                      onMapeo={variables.cambiarMapeo}
                      errores={variables.erroresMapeo}
                      sinResolver={variables.faltanDecisiones}
                      calculando={variables.calculandoVars}
                      error={variables.errorVars}
                      onReintentar={variables.reintentar}
                      chequeo={variables.chequeoVars}
                      fatigaDias={config.fatigaDias ?? 0}
                      fatigaMaximo={config.fatigaMaximo ?? 1}
                      previa={variables.previa}
                    />
                  </div>
                )}
              </>
            )}

            {paso === "cuando" && (
              <PasoCuando
                elegirCuando={elegirCuando}
                estadoEnVivo={borrador.estadoGuardada !== "approved" ? estadoEnVivoDe(guardadaId, borrador.estadoGuardada, borrador.detalleGuardada, borrador.resumenGuardada) : null}
                horarioEd={borrador.horarioEd}
                onHorario={borrador.setHorarioEd}
                onElegirFranja={(conFranja) => borrador.setHorarioEd((h) => ({ ...h, conFranja }))}
                errores={confirmacion.horario.errores}
                limiteMeta={confirmacion.horario.limiteMeta}
                minHasta={cuando.limites.min.slice(0, 10)}
                franjasMarcaTexto={horarioYBaja.franjasMarcaTexto}
                baja={horarioYBaja.bajaEnPantalla}
                onBaja={borrador.setEleccionBaja}
                trae={borrador.detalleGuardada ? horarioYBaja.bajaDeLaPlantilla : null}
                botonBaja={config.botonBaja}
                pideOptOut={horarioYBaja.pideOptOut}
                onConfirmarOptOut={horarioYBaja.confirmarOptOut}
                confirmandoOptOut={horarioYBaja.confirmandoOptOut}
                errorOptOut={horarioYBaja.errorOptOut}
                rutaConfig={`${RUTA_CONFIG}?seccion=cuenta`}
              />
            )}

            {paso === "revisar" && (
              <PasoConfirmar
                campana={campana}
                detalle={borrador.detalleGuardada}
                audiencias={borrador.audiencias}
                chequeo={chequeo}
                calculando={confirmacion.calculando}
                errorChequeo={confirmacion.errorChequeo}
                errorChequeoPaso={confirmacion.errorChequeoPaso}
                chequeoViejo={confirmacion.chequeoViejo}
                onRecalcular={confirmacion.recalcular}
                avisoDesactualizado={confirmacion.avisoDesactualizado}
                rechazo={confirmacion.rechazo}
                onConfirmarDeNuevo={() => void confirmacion.cta.onSiguiente()}
                onAveriguar={() => void confirmacion.averiguar()}
                averiguando={confirmacion.averiguando}
                cuando={cuando}
                elegirCuando={cuando.elegirEnRevisar ? elegirCuando : null}
                sinChequeo={cuando.fechaFaltante ? "Falta elegir la fecha y la hora del envío." : confirmacion.horario.incompleto ? "El horario de la campaña tiene un error." : ""}
                onUsarHora={cuando.usarHora}
                prueba={(
                  <PasoPrueba
                    costoPorMensajeUSD={borrador.resumenGuardada?.costoPorMensajeClienteUSD ?? borrador.detalleGuardada?.costoPorMensajeClienteUSD}
                    saldoUSD={cuenta?.saldoUSD}
                    modo={modo}
                    numero={prueba.numero}
                    onNumero={prueba.setNumero}
                    enviando={prueba.enviando}
                    onEnviar={() => void prueba.enviar(borrador.idCampana)}
                    error={prueba.error}
                    vista={prueba.vista}
                    titulo={prueba.titulo}
                    previa={borrador.detalleGuardada}
                    consultando={prueba.consultando}
                    vencida={prueba.vencida}
                    pollDetenido={prueba.pollDetenido}
                    onReconsultar={prueba.reconsultar}
                    sinPrueba={prueba.sinPrueba}
                    onSinPrueba={prueba.setSinPrueba}
                    deOtra={copiaDe ? textoPruebaDeLaCopia(copiaDe.nombre) : null}
                    deLaSerie={guardadaId ? textoPruebaDeLaSerie(campana?.pruebaDeLaSerie, guardadaId) : null}
                  />
                )}
                franjas={franjas}
                hoy={confirmacion.ahora}
                previa={variables.previa}
                previaConfirmar={confirmacion.previaConfirmar}
                onReintentarPrevia={() => confirmacion.setPreviaConfirmar("")}
                cantidadEscrita={confirmacion.cantidadEscrita}
                onCantidadEscrita={confirmacion.setCantidadEscrita}
                horario={horario}
                franjasMarca={horarioYBaja.franjasMarcaTexto}
                eleccionBaja={borrador.bajaGuardada}
                botonBaja={config.botonBaja}
                onCambiarHorario={() => void saltarA("cuando")}
                fatigaDias={config.fatigaDias}
                fatigaMaximo={config.fatigaMaximo ?? 1}
                recargoPct={config.recargoPct}
                campos={borrador.campos}
                onIrAPaso={saltarA}
              />
            )}
          </section>

          <BarraDelPaso
            cta={ctas[paso]}
            onAtras={atras}
            primero={idx(paso) === 0}
            ocupado={borrador.guardando || confirmacion.confirmando}
            envio={paso === "revisar"}
            confirmando={confirmacion.confirmando}
            // Una línea: el detalle del cobro va plegado en «Cómo se cobra» de «Vas a mandar» (en el celular, la barra crecía).
            pie={paso === "revisar" && chequeo ? (
              <p className="mt-1.5 text-center text-[11px] text-slate-500" data-testid="linea-cobro">
                Se descuentan hasta {usd(chequeo.costo.clienteMaximoUSD)} del saldo a medida que salen.
              </p>
            ) : null}
            resumen={<ResumenEnLaBarra corto={tuCampana.datos.corto} abierto={panelAbierto} onAbrir={() => setPanelAbierto(true)} />}
          />
        </div>

        <aside className="hidden lg:block lg:sticky lg:top-4">{panel()}</aside>
      </div>
      {panelAbierto && <TuCampanaEnElCelular onCerrar={cerrarPanel}>{panel(cerrarPanel)}</TuCampanaEnElCelular>}
    </div>
  );
}
